/**
 * The Workforce Structures route table (FSD 1.2.5).
 *
 * `web/src/router/index.ts` and `web/src/nav/parts/p01-enterprise.ts` are
 * shared files sibling C14 packets also touch and this packet does NOT own —
 * the orchestrator spreads `WORKFORCE_ROUTES` into the router and adds the
 * nav entries once every packet in this batch has landed.
 *
 * Every route is gated on a **read** permission where one exists. Headcount
 * has none of its own (it reuses `org.position.read` — see `permissions.ts`),
 * so its route is gated on that. Naming templates, reorganisations create and
 * reverse are all checked on the action itself, so an Auditor holding only
 * the read grants can open every screen here and change nothing.
 */

import type { RouteRecordRaw } from 'vue-router'

import { WORKFORCE_PERMISSIONS } from '@/modules/enterprise/workforce/permissions'
import { ORG_PERMISSIONS } from '@/modules/org/permissions'

export const WORKFORCE_BASE = '/enterprise/workforce'

export function reorganisationsRoute(): string {
  return `${WORKFORCE_BASE}/reorganisations`
}

export function reorganisationRoute(id: number): string {
  return `${WORKFORCE_BASE}/reorganisations/${id}`
}

export function workforceModelRoute(): string {
  return `${WORKFORCE_BASE}/model`
}

export function departmentsRoute(): string {
  return `${WORKFORCE_BASE}/departments`
}

export function assignmentValidatorRoute(): string {
  return `${WORKFORCE_BASE}/assignment-validator`
}

/*
 * `naming-templates` and `headcount` no longer have routes of their own (R0):
 * the "Model, Naming & Headcount" screen hosts the same panels as tabs
 * (`?tab=naming`, `?tab=headcount`), and `router/legacyRedirects.ts` sends the
 * two old paths there.
 *
 * Model is gated on `org.naming_template.read` — the grant the old
 * `naming-templates` route required — so everyone who could reach naming
 * templates before still can. The ideal gate is "naming_template.read OR
 * position.read", but `meta.permission` (router/index.ts) takes one code
 * only; the view hides the model/headcount tabs (positions) per
 * `org.position.read` and the naming tab per `org.naming_template.read`.
 * Departments on `org.unit.read`, the validator on `org.assignment.read`.
 */
export const WORKFORCE_ROUTES: RouteRecordRaw[] = [
  {
    path: workforceModelRoute(),
    name: 'enterprise-workforce-model',
    component: () => import('@/modules/enterprise/workforce/WorkforceModelView.vue'),
    meta: {
      titleKey: 'enterprise.workforce.routeTitles.model',
      permission: WORKFORCE_PERMISSIONS.namingTemplateRead,
    },
  },
  {
    path: departmentsRoute(),
    name: 'enterprise-departments',
    component: () => import('@/modules/enterprise/workforce/DepartmentsView.vue'),
    meta: {
      titleKey: 'enterprise.workforce.routeTitles.departments',
      permission: ORG_PERMISSIONS.unitRead,
    },
  },
  {
    path: assignmentValidatorRoute(),
    name: 'enterprise-assignment-validator',
    component: () => import('@/modules/enterprise/workforce/AssignmentValidatorView.vue'),
    meta: {
      titleKey: 'enterprise.workforce.routeTitles.assignmentValidator',
      permission: ORG_PERMISSIONS.assignmentRead,
    },
  },
  {
    path: reorganisationsRoute(),
    name: 'enterprise-workforce-reorganisations',
    component: () => import('@/modules/enterprise/workforce/ReorganisationsView.vue'),
    meta: {
      titleKey: 'enterprise.workforce.routeTitles.reorganisations',
      permission: WORKFORCE_PERMISSIONS.reorganisationRead,
    },
  },
  {
    path: `${WORKFORCE_BASE}/reorganisations/:id(\\d+)`,
    name: 'enterprise-workforce-reorganisation',
    component: () => import('@/modules/enterprise/workforce/ReorganisationView.vue'),
    meta: {
      titleKey: 'enterprise.workforce.routeTitles.reorganisation',
      permission: WORKFORCE_PERMISSIONS.reorganisationRead,
    },
  },
]
